import type { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { type Achievement, achievementsSelectors } from '../achievements';
import {
  type AchievementCompletionStatus,
  MILESTONE_REDUCER_KEY,
  type Milestone,
  type MilestonesStore,
  type UpNextMilestonesResult,
} from './milestonesTypes';

// Whatever getAchievementsBySortId reads, privacy mode's slices included.
type State = Parameters<typeof achievementsSelectors.getAchievementsBySortId>[0] & {
  [MILESTONE_REDUCER_KEY]: MilestonesStore;
};
type AchievementIdProps = { achievementId?: string };
type MilestonesByAchievementId = ImmutableMap<string | undefined, ImmutableList<Milestone>>;

const getAchievementsBySortId = (state: State): ImmutableList<Achievement> | undefined =>
  achievementsSelectors.getAchievementsBySortId(state);

const getRequiredMilestoneList = (milestoneList: ImmutableList<Milestone> | undefined): ImmutableList<Milestone> => {
  if (!milestoneList) {
    throw new TypeError("Cannot read properties of undefined (reading 'forEach')");
  }
  return milestoneList;
};

const parseOptionalIsoDate = (date: string | undefined): DateTime =>
  date === undefined ? DateTime.invalid('missing ISO date') : DateTime.fromISO(date);

const getMilestoneProgress = (milestone: Milestone): number =>
  Number(milestone.currentAmount || milestone.currentCount) / Number(milestone.targetAmount || milestone.targetCount);

export const getResourceStore = (state: State): MilestonesStore => state[MILESTONE_REDUCER_KEY];
export const getLastSyncDate = (state: State): string | null | undefined => getResourceStore(state).lastSyncDate;
export const getMilestonesById = (state: State): ImmutableMap<string, Milestone> =>
  getResourceStore(state).resourcesById;
export const getLoadPending = (state: State): boolean => getResourceStore(state).loadPending;
export const getIsLoading = (state: State): boolean => getResourceStore(state).isLoading;
export const getError = (state: State): unknown => getResourceStore(state).error;

export const getMilestonesByAchievementId: (state: State) => MilestonesByAchievementId | undefined = createSelector(
  getMilestonesById,
  (achievements) => {
    // group milestones into 'achievements' groups
    const sortedAchievements = achievements
      ?.toList()
      .groupBy((x) => x.achievementId)
      .map((row) => row.toList())
      .toMap();
    // sort the milestones in the resulting ordered map
    return sortedAchievements?.withMutations((achievement) => {
      achievement.forEach((row, key) =>
        achievement.set(
          key,
          row.sort((a, b) => (Number(a.knownMilestoneItemId) < Number(b.knownMilestoneItemId) ? -1 : 1)),
        ),
      );
    });
  },
);

export const isAchievementCompletedOrStarted: (state: State, props: AchievementIdProps) => AchievementCompletionStatus =
  createSelector(
    getMilestonesByAchievementId,
    (_state: State, props: AchievementIdProps) => props.achievementId,
    (achievements, id): AchievementCompletionStatus => {
      const milestoneList = getRequiredMilestoneList(achievements?.get(id));
      let count = 0;
      milestoneList.forEach((milestone) => {
        if (milestone?.status === 'COMPLETED') {
          count += 1;
        }
      });
      return {
        isCompleted: count >= milestoneList.size,
        justStarted: count === 1,
      };
    },
  );

export const getUpNextMilestones: (state: State) => UpNextMilestonesResult = createSelector(
  getAchievementsBySortId,
  getMilestonesByAchievementId,
  (sortOrder, achievementsMap): UpNextMilestonesResult => {
    const recentlyEarned: Milestone[] = [];
    const milestoneArr: { milestone: Milestone }[] = [];
    let count = 0;
    let total = 0;
    sortOrder?.forEach((achievement) => {
      const milestoneRow = achievementsMap?.get(achievement?.id ?? undefined);
      let hasIterated = false;
      milestoneRow?.forEach((milestone) => {
        total += 1;
        if (milestone?.completedOn) {
          count += 1;
          const completedOn = DateTime.fromISO(milestone?.completedOn);
          if (completedOn) {
            const modifiedAt = parseOptionalIsoDate(milestone?.modifiedAt);
            const index = recentlyEarned.findIndex((item) => parseOptionalIsoDate(item?.modifiedAt) < modifiedAt);
            recentlyEarned.splice(index, 0, milestone);
          }
        }
        if (milestone?.type === 'RANDOM') {
          hasIterated = true;
        }
        if (milestone?.status === 'IN_PROGRESS' && !hasIterated) {
          const milestonePercentage = getMilestoneProgress(milestone);
          const index = milestoneArr.findIndex((mItem) => getMilestoneProgress(mItem.milestone) < milestonePercentage);
          if (index === -1) {
            milestoneArr.push({ milestone });
          } else {
            milestoneArr.splice(index, 0, { milestone });
          }
          hasIterated = true;
        }
        if (milestone?.status === 'NOT_STARTED' && !hasIterated) {
          milestoneArr.push({ milestone });
          hasIterated = true;
        }
        return true;
      });
    });
    return { milestoneArr, recentlyEarned, count, total };
  },
);
